// Carry Roll LaunchInputs — collapsible Launch & Delivery inputs (2.1-2.5 + advanced 2.6,2.7,3.x).

import type { ReactNode } from 'react';
import { ScrubInput } from 'calc-controls';
import { Collapsible } from './Collapsible';
import { SmashFactor } from './SmashFactor';
import { HelpTooltip } from './HelpTooltip';
import { useStore } from '../../store';
import { useInputMode, useValidation } from '../../store/selectors';
import { degToRad, radToDeg, msToMph, mphToMs, msToKph, kphToMs } from '@carry-roll/shared-logic';
import { InputMode } from '@carry-roll/shared-types';

export function LaunchInputs() {
  const launch = useStore((s) => s.request.launch);
  const setLaunch = useStore((s) => s.setLaunch);
  const speedUnit = useStore((s) => s.speedUnit);
  const inputMode = useInputMode();
  const advanced = inputMode === InputMode.Advanced;

  const warnings = useValidation().filter((w) => w.field.startsWith('launch.'));
  const fieldError = (field: string) => warnings.find((w) => w.field === field)?.message;

  return (
    <Collapsible title="Launch & Delivery" testId="launch-inputs" helpLabel="Launch and delivery inputs">
      <LabeledNumber
        label={`Ball speed (${speedUnit})`}
        ariaLabel="Ball speed"
        value={speedUnit === 'mph' ? msToMph(launch.ballSpeed) : msToKph(launch.ballSpeed)}
        onChange={(v) => setLaunch({ ballSpeed: speedUnit === 'mph' ? mphToMs(v) : kphToMs(v) })}
        error={fieldError('launch.ballSpeed')}
        step={0.1}
        help="The speed of the ball immediately after impact. The biggest driver of carry distance."
      />
      <LabeledNumber
        label={`Club speed (${speedUnit})`}
        ariaLabel="Club speed"
        value={launch.clubSpeed == null ? 0 : speedUnit === 'mph' ? msToMph(launch.clubSpeed) : msToKph(launch.clubSpeed)}
        onChange={(v) =>
          setLaunch({ clubSpeed: v === 0 ? null : speedUnit === 'mph' ? mphToMs(v) : kphToMs(v) })
        }
        step={0.1}
        help="Clubhead speed. Ball speed ÷ club speed = smash factor (1.00–1.55)."
      />
      <SmashFactor />
      <LabeledNumber
        label={`Launch angle (${radToDeg(launch.launchAngle).toFixed(1)}°)`}
        ariaLabel="Launch angle in degrees"
        value={radToDeg(launch.launchAngle)}
        onChange={(v) => setLaunch({ launchAngle: degToRad(v) })}
        error={fieldError('launch.launchAngle')}
        step={0.1}
        help="Vertical angle of the ball at launch. Optimal driver launch is 10–16°."
      />
      <LabeledNumber
        label="Spin rate (rpm)"
        ariaLabel="Spin rate"
        value={launch.spinRpm}
        onChange={(v) => setLaunch({ spinRpm: v })}
        error={fieldError('launch.spinRpm')}
        step={50}
        help="Backspin in RPM. Governs lift, hang time, and landing angle."
      />
      {advanced && (
        <>
          <LabeledNumber
            label="Spin axis (deg)"
            ariaLabel="Spin axis"
            value={launch.spinAxisDeg}
            onChange={(v) => setLaunch({ spinAxisDeg: v })}
            step={0.5}
            help="Tilt of the spin axis. Positive fades right; negative draws/hooks left. ±2° reads as straight."
          />
          <LabeledNumber
            label="Launch direction (deg)"
            ariaLabel="Launch direction azimuth"
            value={launch.launchDirDeg}
            onChange={(v) => setLaunch({ launchDirDeg: v })}
            step={0.5}
            help="Azimuth of the initial launch direction. 0 is at the target; + is right, − is left."
          />
        </>
      )}
    </Collapsible>
  );
}

interface LabeledNumberProps {
  label: string;
  ariaLabel: string;
  value: number | string;
  onChange: (v: number) => void;
  error?: string | undefined;
  step?: number;
  help?: ReactNode;
}

function decimalsForStep(step?: number): number {
  if (!step || step >= 1) return 0;
  if (step >= 0.1) return 1;
  return 2;
}

export function LabeledNumber({ label, ariaLabel, value, onChange, error, step, help }: LabeledNumberProps) {
  const numValue = typeof value === 'number' ? value : 0;
  return (
    <div className="block">
      <span className="field-label">
        {label}
        {help != null && <HelpTooltip label={ariaLabel}>{help}</HelpTooltip>}
      </span>
      <ScrubInput
        inline
        label={ariaLabel}
        value={numValue}
        onChange={onChange}
        step={step ?? 1}
        decimals={decimalsForStep(step)}
      />
      {error && (
        <span className="text-xs" style={{ color: 'var(--color-danger)' }} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
